import { useState, useCallback, useRef } from 'react'
import { LimitExceededError } from '../lib/api'

// ── Modello AI ────────────────────────────────────────────────────────────────
// Il toggle modello è fuori scope ma il parametro è già esposto su sendMessage
// per renderne banale l'aggiunta in futuro.
export const AI_MODEL = 'claude-sonnet-4-6'

// ── Tipi ─────────────────────────────────────────────────────────────────────

export type MessageRole = 'user' | 'assistant'

export interface ChatMessage {
  id: string
  role: MessageRole
  content: string
  /** true mentre l'assistente sta ancora streamando */
  streaming?: boolean
  timestamp: Date
}

interface UseAiChatReturn {
  messages: ChatMessage[]
  isStreaming: boolean
  error: string | null
  limitError: LimitExceededError | null
  sendMessage: (text: string, model?: string) => Promise<void>
  clearHistory: () => void
}

// ── Hook ─────────────────────────────────────────────────────────────────────

export function useAiChat(): UseAiChatReturn {
  const [messages, setMessages] = useState<ChatMessage[]>([])
  const [isStreaming, setIsStreaming] = useState(false)
  const [error, setError] = useState<string | null>(null)
  const [limitError, setLimitError] = useState<LimitExceededError | null>(null)
  const abortRef = useRef<AbortController | null>(null)

  const sendMessage = useCallback(async (text: string, model: string = AI_MODEL) => {
    if (!text.trim() || isStreaming) return

    setError(null)
    setLimitError(null)

    const userMsg: ChatMessage = {
      id: crypto.randomUUID(),
      role: 'user',
      content: text.trim(),
      timestamp: new Date(),
    }

    const assistantMsgId = crypto.randomUUID()
    const assistantMsg: ChatMessage = {
      id: assistantMsgId,
      role: 'assistant',
      content: '',
      streaming: true,
      timestamp: new Date(),
    }

    setMessages((prev) => [...prev, userMsg, assistantMsg])
    setIsStreaming(true)

    const abort = new AbortController()
    abortRef.current = abort

    try {
      const apiBase = import.meta.env.VITE_API_URL ?? 'http://localhost:8000'
      const res = await fetch(`${apiBase}/api/v1/ai/chat`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ message: text.trim(), model }),
        signal: abort.signal,
      })

      if (!res.ok || !res.body) {
        throw new Error(`HTTP ${res.status}`)
      }

      const reader = res.body.getReader()
      const decoder = new TextDecoder()
      let buffer = ''

      while (true) {
        const { done, value } = await reader.read()
        if (done) break

        buffer += decoder.decode(value, { stream: true })

        // Processa ogni riga SSE nel buffer
        const lines = buffer.split('\n')
        buffer = lines.pop() ?? ''

        for (const line of lines) {
          if (!line.startsWith('data:')) continue
          const payload = line.slice(5).trim()
          if (payload === '[DONE]') {
            // Stream terminato
            setMessages((prev) =>
              prev.map((m) =>
                m.id === assistantMsgId ? { ...m, streaming: false } : m,
              ),
            )
            setIsStreaming(false)
            return
          }
          try {
            const parsed = JSON.parse(payload) as { delta?: string }
            if (parsed.delta) {
              setMessages((prev) =>
                prev.map((m) =>
                  m.id === assistantMsgId
                    ? { ...m, content: m.content + parsed.delta }
                    : m,
                ),
              )
            }
          } catch {
            // Linea SSE non JSON — ignora
          }
        }
      }

      // Stream terminato senza [DONE] esplicito
      setMessages((prev) =>
        prev.map((m) =>
          m.id === assistantMsgId ? { ...m, streaming: false } : m,
        ),
      )
    } catch (err) {
      if ((err as Error).name === 'AbortError') return

      if (err instanceof LimitExceededError) {
        setLimitError(err)
      } else {
        setError("Impossibile ottenere una risposta. Riprova tra qualche istante.")
      }
      setMessages((prev) =>
        prev.map((m) =>
          m.id === assistantMsgId
            ? { ...m, content: "Si è verificato un errore. Riprova.", streaming: false }
            : m,
        ),
      )
    } finally {
      setIsStreaming(false)
    }
  }, [isStreaming])

  const clearHistory = useCallback(() => {
    abortRef.current?.abort()
    setMessages([])
    setError(null)
    setLimitError(null)
    setIsStreaming(false)
  }, [])

  return { messages, isStreaming, error, limitError, sendMessage, clearHistory }
}
